import React, { useState } from 'react';

// Purely presentational, data-driven task list ("Task Rows" pattern).
// Renders one row per `tasks[]` entry: a leading badge (idle ring w/ row
// number -> spinning ring -> filled check/x), the label, an optional
// right-aligned amount, an optional status pill, and an optional expandable
// details grid. No stream/course logic lives here — callers (e.g.
// CourseRequestForm's stages -> tasks adapter) own that mapping.
//
// tasks: [{ key, label, status: 'pending'|'active'|'done'|'failed', amount?, pill?, details?: [{ label, meta }] }]

const XIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const CheckIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const RetryIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />
  </svg>
);

function ChevronIcon({ open }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transition: 'transform 0.3s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// 24px ring: base track in --border-subtle, active arc in --text-tertiary.
// Reuses the codebase-wide `graspd-spin` keyframe (theme.css) for the spin.
function SpinnerRing({ active, children }) {
  const size = 24;
  const stroke = 2;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <span
      role={active ? 'status' : undefined}
      aria-label={active ? 'In progress' : undefined}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      <svg
        aria-hidden="true"
        width={size}
        height={size}
        style={{ position: 'absolute', top: 0, left: 0, ...(active ? { animation: 'graspd-spin 1.1s linear infinite' } : null) }}
      >
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border-subtle)" strokeWidth={stroke} />
        {active && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--text-tertiary)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${c * 0.28} ${c * 0.72}`}
          />
        )}
      </svg>
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          fontSize: 10.5, // between --text-label-size(11) and nothing smaller on the scale; a token here reads too large in a 24px ring
          fontWeight: 600,
          color: 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {children}
      </span>
    </span>
  );
}

// Filled 22px circle badge for terminal states (done/failed).
function Badge({ tone, children }) {
  const bg = tone === 'red' ? 'var(--danger)' : 'var(--accent-primary)';
  return (
    <span
      aria-hidden="true"
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: bg,
        color: 'var(--text-inverted)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        animation: 'taskRowsPopIn 300ms cubic-bezier(0.23,1,0.32,1) both',
      }}
    >
      {children}
    </span>
  );
}

function TaskBadge({ status, number }) {
  if (status === 'done') return <Badge tone="green">{CheckIcon}</Badge>;
  if (status === 'failed') return <Badge tone="red">{XIcon}</Badge>;
  if (status === 'active') return <SpinnerRing active>{number}</SpinnerRing>;
  return <SpinnerRing>{number}</SpinnerRing>; // pending: idle ring showing the row number
}

function StatusPill({ tone, children }) {
  const bg = tone === 'red' ? 'var(--danger-bg)' : 'var(--accent-light)';
  const color = tone === 'red' ? 'var(--danger)' : 'var(--accent-text)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 22,
        borderRadius: 'var(--radius-full)',
        padding: '0 8px',
        fontSize: 'var(--text-label-size)',
        fontWeight: 500,
        background: bg,
        color,
        flexShrink: 0,
        animation: 'taskRowsFadeIn 200ms ease-out both',
      }}
    >
      {children}
    </span>
  );
}

function TaskPill({ task, onRetry }) {
  if (!task.pill) return null;
  const isFailed = task.status === 'failed';
  const tone = isFailed ? 'red' : 'green';
  return (
    <StatusPill tone={tone}>
      {task.pill}
      {isFailed &&
        (onRetry ? (
          <button
            type="button"
            aria-label="Retry"
            data-testid={`task-retry-${task.key}`}
            onClick={(e) => {
              e.stopPropagation();
              onRetry(task.key);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              color: 'inherit',
              cursor: 'pointer',
              animation: 'graspd-spin 1.2s linear infinite',
            }}
          >
            {RetryIcon}
          </button>
        ) : (
          <span aria-hidden="true" data-testid={`task-retry-${task.key}`} style={{ display: 'flex', animation: 'graspd-spin 1.2s linear infinite' }}>
            {RetryIcon}
          </span>
        ))}
    </StatusPill>
  );
}

export default function TaskRows({ tasks = [], variant = 'Card', onRetry }) {
  const [openKeys, setOpenKeys] = useState({});
  const isList = variant === 'List';

  const toggle = (key) => setOpenKeys((cur) => ({ ...cur, [key]: !cur[key] }));

  return (
    <>
      <style>{`
        @keyframes taskRowsFadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes taskRowsPopIn {
          0% { opacity: 0; transform: scale(0.5); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes taskRowsFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .task-row-header[data-expandable]:hover {
          background: var(--nav-item-hover);
        }
      `}</style>
      <div
        role="list"
        aria-label="Task progress"
        style={
          isList
            ? {
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-panel)',
                overflow: 'hidden',
              }
            : { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', width: '100%' }
        }
      >
        {tasks.map((task, i) => {
          const open = !!openKeys[task.key];
          const hasDetails = Array.isArray(task.details) && task.details.length > 0;
          return (
            <div
              key={task.key}
              role="listitem"
              data-testid={`task-${task.key}`}
              data-status={task.status}
              style={{
                overflow: 'hidden',
                // 22 (collapsed) / 14 (open) are a deliberate expand-animation
                // pairing lifted from the reference design, not values on our
                // --radius-* scale — kept as literals on purpose.
                borderRadius: isList ? 0 : open ? 14 : 22,
                background: isList ? 'transparent' : 'var(--bg-elevated)',
                boxShadow: isList ? 'none' : 'var(--shadow-sm)',
                borderBottom: isList ? '1px solid var(--border-subtle)' : 'none',
                transition: 'border-radius 0.3s ease',
                animation: `taskRowsFadeUp 450ms cubic-bezier(0.23,1,0.32,1) ${i * 80}ms both`,
              }}
            >
              {/* The retry control inside TaskPill is a real interactive
                  <button>, so the expand toggle and the chevron are separate
                  sibling <button>s rather than one row-wide button — nesting
                  a <button> inside a <button> is invalid HTML and gets
                  flagged by React's DOM-nesting validation. */}
              <div className="task-row-header" data-expandable={hasDetails || undefined} style={{ display: 'flex', alignItems: 'center', height: 44, padding: '0 var(--space-3)', boxSizing: 'border-box', borderRadius: 'inherit' }}>
                <button
                  type="button"
                  className="task-row-toggle"
                  aria-expanded={hasDetails ? open : undefined}
                  onClick={hasDetails ? () => toggle(task.key) : undefined}
                  disabled={!hasDetails}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: hasDetails ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
                    <TaskBadge status={task.status} number={i + 1} />
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 'var(--text-body-size)',
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {task.label}
                  </span>
                  {task.amount && (
                    <span
                      style={{
                        fontSize: 'var(--text-numeric-size)',
                        color: 'var(--text-secondary)',
                        fontVariantNumeric: 'tabular-nums',
                        flexShrink: 0,
                      }}
                    >
                      {task.amount}
                    </span>
                  )}
                </button>
                <span style={{ marginLeft: 'var(--space-3)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <TaskPill task={task} onRetry={onRetry} />
                </span>
                {hasDetails && (
                  <button
                    type="button"
                    aria-label={open ? 'Collapse details' : 'Expand details'}
                    onClick={() => toggle(task.key)}
                    style={{
                      marginLeft: 'var(--space-1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      flexShrink: 0,
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    <ChevronIcon open={open} />
                  </button>
                )}
              </div>

              {hasDetails && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateRows: open ? '1fr' : '0fr',
                    opacity: open ? 1 : 0,
                    transition: 'grid-template-rows 0.3s cubic-bezier(0.23,1,0.32,1), opacity 0.3s cubic-bezier(0.23,1,0.32,1)',
                  }}
                >
                  <div style={{ overflow: 'hidden' }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '24px 1fr',
                        gap: 'var(--space-3)',
                        padding: '0 var(--space-3) var(--space-3)',
                        marginTop: 'var(--space-1)',
                      }}
                    >
                      <span aria-hidden="true" style={{ margin: '0 auto', width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)' }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {task.details.map((d, j) => (
                          <div
                            key={d.label}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              ...(open ? { animation: `taskRowsFadeUp 300ms cubic-bezier(0.23,1,0.32,1) ${120 + j * 100}ms both` } : null),
                            }}
                          >
                            <span style={{ fontSize: 'var(--text-numeric-size)', color: 'var(--text-secondary)' }}>{d.label}</span>
                            <span
                              style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 11.5, // between --text-label-size(11) and --text-numeric-size(12); either token distorts this meta column
                                color: 'var(--text-tertiary)',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {d.meta}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
